import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../../config/api';
import { ClipboardList, DollarSign, XCircle, CheckCircle, Clock, Users, X, Scissors, Undo2 } from 'lucide-react';
import ReciboManager from './ReciboManager';
import { formatEcuadorTime, getOrderCode } from '../../utils/ecuadorTime';
import CustomAlert from './shared/CustomAlert';

// ⭐ Zona horaria + código de pedido por día ahora viven en un solo lugar
// compartido (utils/ecuadorTime.js) -- ver ese archivo para el porqué.
const getDayCode = getOrderCode;

const PERSON_COLORS = [
  { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-400', btn: 'bg-blue-600 hover:bg-blue-700', label: 'bg-blue-600' },
  { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-400', btn: 'bg-purple-600 hover:bg-purple-700', label: 'bg-purple-600' },
  { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-400', btn: 'bg-orange-600 hover:bg-orange-700', label: 'bg-orange-600' },
  { bg: 'bg-pink-100', text: 'text-pink-700', border: 'border-pink-400', btn: 'bg-pink-600 hover:bg-pink-700', label: 'bg-pink-600' },
];

function PedidosPendientes({ token, onNavigateTo, clientData }) {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [tables, setTables] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [pendingReceipt, setPendingReceipt] = useState(null);

  // ── Pago normal ──
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [cashReceived, setCashReceived] = useState('');
  const [mixedCash, setMixedCash] = useState('');
  const [mixedTransfer, setMixedTransfer] = useState('');
  const [customAlert, setCustomAlert] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [confirmRemoveItem, setConfirmRemoveItem] = useState(null); // { orderId, origIdx, name, quantity }
  const [removingItem, setRemovingItem] = useState(false);

  // ── Desglosar cuenta ──
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitOrder, setSplitOrder] = useState(null);
  const [splitPersons, setSplitPersons] = useState([]);
  const [splitPayingIdx, setSplitPayingIdx] = useState(null); // índice de persona pagando
  const [splitPayMethod, setSplitPayMethod] = useState('cash');
  const [splitCash, setSplitCash] = useState('');
  const [splitTransfer, setSplitTransfer] = useState(''); // eslint-disable-line no-unused-vars
  const [splitMixedCash, setSplitMixedCash] = useState('');
  const [splitMixedTransfer, setSplitMixedTransfer] = useState('');
  const [splitProcessing, setSplitProcessing] = useState(false);
  const [splitUnits, setSplitUnits] = useState([]); // ítems explotados por unidad
  const [splitHistory, setSplitHistory] = useState([]); // historial para deshacer
  const [showAssignModal, setShowAssignModal] = useState(null); // orderId para asignar mesa

  const showAlert = (type, title, message) => {
    setCustomAlert({ type, title, message });
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedOrder(null);
    setCashReceived('');
    setMixedCash('');
    setMixedTransfer('');
    setPaymentMethod('cash');
  };

  useEffect(() => {
    loadPendingOrders();
    loadTables();
    const interval = setInterval(() => {
      loadPendingOrders();
      loadTables();
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadPendingOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/pos/sales`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const pending = response.data.filter(
        sale => sale.payment_status === 'pending' && !sale.cancelled
      );
      setPendingOrders(pending);
    } catch (error) {
      console.error('Error al cargar pedidos pendientes:', error);
    }
  };

  const loadTables = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/pos/tables`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTables(response.data);
    } catch (error) {
      console.error('Error al cargar mesas:', error);
    }
  };

  const handleAssignTable = async (orderId, tableId) => {
    try {
      await axios.post(
        `${API_URL}/api/pos/sales/${orderId}/assign-table`,
        { table_id: tableId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showAlert('success', 'Mesa Asignada', ' Pedido asignado a mesa exitosamente');
      loadPendingOrders();
      loadTables();
    } catch (error) {
      showAlert('error', 'Error', error.response?.data?.error || 'No se pudo asignar la mesa');
    }
  };

  // ⭐ Anular UN producto especifico del pedido (ej: se acabó ese plato),
  // sin tener que anular todo el pedido. Si era el último producto, el
  // backend anula el pedido completo automáticamente.
  const executeRemoveItem = async () => {
    if (!confirmRemoveItem) return;
    setRemovingItem(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/pos/sales/${confirmRemoveItem.orderId}/remove-item`,
        { origIdx: confirmRemoveItem.origIdx, quantity: confirmRemoveItem.quantity },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showAlert(
        'success',
        response.data.cancelled ? 'Pedido anulado' : 'Producto anulado',
        response.data.cancelled
          ? `"${confirmRemoveItem.name}" era el único producto del pedido; el pedido quedó anulado.`
          : `"${confirmRemoveItem.name}" fue quitado del pedido.`
      );
      setConfirmRemoveItem(null);
      loadPendingOrders();
      loadTables();
    } catch (error) {
      showAlert('error', 'Error', error.response?.data?.error || 'No se pudo anular el producto');
    } finally {
      setRemovingItem(false);
    }
  };

  const handlePayOrder = (order) => {
    setSelectedOrder(order);
    setPaymentMethod('cash');
    setCashReceived('');
    setMixedCash('');
    setMixedTransfer('');
    setShowPaymentModal(true);
  };

  // ⭐ FIX: endpoint correcto /complete (no /confirm-payment)
  const processPayment = async () => {
    if (!selectedOrder) return;
    const total = selectedOrder.total;

    if (paymentMethod === 'cash') {
      const received = parseFloat(cashReceived) || 0;
      if (received < total) {
        showAlert('error', 'Monto Insuficiente', ` Recibido: $${received.toFixed(2)}\n Total: $${total.toFixed(2)}`);
        return;
      }
    } else if (paymentMethod === 'mixed') {
      const cash = parseFloat(mixedCash) || 0;
      const transfer = parseFloat(mixedTransfer) || 0;
      if (cash + transfer < total) {
        showAlert('error', 'Monto Insuficiente', ` Recibido: $${(cash + transfer).toFixed(2)}\n Total: $${total.toFixed(2)}`);
        return;
      }
    }

    try {
      const change = paymentMethod === 'cash'
        ? Math.max(0, (parseFloat(cashReceived) || 0) - total)
        : paymentMethod === 'mixed'
        ? Math.max(0, ((parseFloat(mixedCash) || 0) + (parseFloat(mixedTransfer) || 0)) - total)
        : 0;

      const cashR = paymentMethod === 'cash' ? parseFloat(cashReceived) : paymentMethod === 'mixed' ? (parseFloat(mixedCash) || 0) : null;
      const transferR = paymentMethod === 'transfer' ? total : paymentMethod === 'mixed' ? (parseFloat(mixedTransfer) || 0) : null;

      await axios.post(
        `${API_URL}/api/pos/sales/${selectedOrder.id}/complete`,
        {
          payment_method: paymentMethod,
          cash_received: cashR,
          // ⭐ FIX (reporte de Angelo, 2026-08-09: "el efectivo esperado toma en
          // cuenta lo que aún no cobro"). `transferR` se calculaba dos líneas más
          // arriba pero NUNCA se mandaba, y el endpoint tampoco lo guardaba: un
          // pedido pendiente cobrado por transferencia quedaba con
          // transfer_amount = 0. El cierre de caja lo sumaba en "Total vendido"
          // (que va por sales.total) pero no en "Ventas transferencia" (que va por
          // transfer_amount), de ahí que efectivo + transferencia no cuadrara con
          // el total. Mismo problema en el desglose por método de pago de Reportes.
          transfer_amount: transferR,
          change_given: change > 0 ? change : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // ⭐ No se cierra el modal aqui: se difiere a onDone (ver
      // ReciboManager), que corre despues de que el usuario decide si
      // imprime el recibo.
      const orderItems = Array.isArray(selectedOrder.items)
        ? selectedOrder.items
        : JSON.parse(selectedOrder.items || '[]');
      setPendingReceipt({
        id: selectedOrder.id,
        items: orderItems.map(it => ({
          name: it.name,
          quantity: it.quantity,
          subtotal: it.subtotal ?? (it.price || 0) * (it.quantity || 1)
        })),
        total,
        payment_method: paymentMethod,
        cash_received: cashR,
        transfer_amount: transferR,
        change_given: change,
        order_type: selectedOrder.order_type,
        customer_name: selectedOrder.customer_name || '',
        created_at: new Date().toISOString(),
        onDone: () => {
          setPendingReceipt(null);
          closePaymentModal();
          loadPendingOrders();
          loadTables();
        }
      });
    } catch (error) {
      showAlert('error', 'Error al Procesar', error.response?.data?.error || 'No se pudo procesar el pago');
    }
  };

  const handleCancelOrder = (orderId) => {
    setConfirmCancel(orderId);
    setCancelReason('');
  };

  const executeCancelOrder = async (orderId) => {
    if (!cancelReason.trim()) {
      showAlert('warning', 'Motivo requerido', 'Debes ingresar el motivo de la anulación');
      return;
    }
    setConfirmCancel(null);
    try {
      await axios.post(
        `${API_URL}/api/pos/sales/${orderId}/cancel`,
        { reason: cancelReason.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showAlert('success', 'Pedido Anulado', `Pedido anulado correctamente\n\nMotivo: ${cancelReason.trim()}`);
      setCancelReason('');
      loadPendingOrders();
      loadTables();
    } catch (error) {
      showAlert('error', 'Error', error.response?.data?.error || 'No se pudo anular el pedido');
    }
  };

  // ── DESGLOSAR CUENTA ──────────────────────────────────────────────

  const openSplitModal = (order) => {
    const items = Array.isArray(order.items) ? order.items : JSON.parse(order.items || '[]');
    // Explotar ítems en unidades individuales para asignación por unidad
    const units = [];
    items.forEach((item, origIdx) => {
      for (let u = 0; u < (item.quantity || 1); u++) {
        units.push({
          ...item,
          quantity: 1,
          subtotal: item.price,
          _origIdx: origIdx,
          _unitNum: u + 1,
          _totalUnits: item.quantity || 1,
        });
      }
    });
    setSplitUnits(units);
    // Asignar todas las unidades a Persona 1 por defecto
    setSplitPersons([
      { id: 1, name: 'Persona 1', itemIndices: units.map((_, i) => i), paymentDone: false },
      { id: 2, name: 'Persona 2', itemIndices: [], paymentDone: false },
    ]);
    setSplitOrder(order);
    setSplitPayingIdx(null);
    setSplitPayMethod('cash');
    setSplitCash('');
    setSplitTransfer('');
    setSplitMixedCash('');
    setSplitMixedTransfer('');
    setSplitHistory([]);
    setShowSplitModal(true);
  };

  const addSplitPerson = () => {
    if (splitPersons.length >= 4) return;
    setSplitPersons(prev => [
      ...prev,
      { id: prev.length + 1, name: `Persona ${prev.length + 1}`, itemIndices: [], paymentDone: false }
    ]);
  };

  const removeSplitPerson = (idx) => {
    if (splitPersons.length <= 2) return;
    // Reasignar ítems de la persona eliminada a Persona 1
    const orphanItems = splitPersons[idx].itemIndices;
    setSplitPersons(prev =>
      prev.filter((_, i) => i !== idx).map((p, i) =>
        i === 0 ? { ...p, itemIndices: [...new Set([...p.itemIndices, ...orphanItems])] } : p
      )
    );
  };

  // Asignar unidad a persona (exclusivo: quita de otros) con historial para deshacer
  const assignItemToPerson = (personIdx, itemIdx) => {
    setSplitHistory(prev => [...prev, JSON.parse(JSON.stringify(splitPersons))]);
    setSplitPersons(prev => prev.map((p, i) => {
      if (i === personIdx) {
        const alreadyHas = p.itemIndices.includes(itemIdx);
        return { ...p, itemIndices: alreadyHas ? p.itemIndices.filter(j => j !== itemIdx) : [...p.itemIndices, itemIdx] };
      } else {
        return { ...p, itemIndices: p.itemIndices.filter(j => j !== itemIdx) };
      }
    }));
  };

  // Deshacer última asignación
  const undoSplitAssignment = () => {
    if (splitHistory.length === 0) return;
    const prev = splitHistory[splitHistory.length - 1];
    setSplitPersons(prev);
    setSplitHistory(h => h.slice(0, -1));
  };

  const getSplitPersonTotal = (personIdx) => {
    if (!splitOrder || splitUnits.length === 0) return 0;
    return splitPersons[personIdx].itemIndices.reduce((sum, idx) => {
      const unit = splitUnits[idx];
      return sum + (unit?.price || 0);
    }, 0);
  };

  const splitRepartirEquitativamente = () => {
    if (!splitOrder || splitUnits.length === 0) return;
    setSplitHistory(prev => [...prev, JSON.parse(JSON.stringify(splitPersons))]);
    const n = splitPersons.length;
    // Distribuir unidades cíclicamente
    const newPersons = splitPersons.map(p => ({ ...p, itemIndices: [] }));
    splitUnits.forEach((_, i) => {
      newPersons[i % n].itemIndices.push(i);
    });
    setSplitPersons(newPersons);
  };

  const handlePaySplitPortion = async (personIdx) => {
    if (!splitOrder || splitUnits.length === 0) return;
    const person = splitPersons[personIdx];
    const total = parseFloat(getSplitPersonTotal(personIdx).toFixed(2));

    if (person.itemIndices.length === 0 || total <= 0) {
      showAlert('warning', 'Sin ítems', 'Esta persona no tiene ítems asignados.');
      return;
    }

    // Validar monto
    if (splitPayMethod === 'cash') {
      const received = parseFloat(splitCash) || 0;
      if (received < total) {
        showAlert('error', 'Monto insuficiente', `Recibido: $${received.toFixed(2)} — Total: $${total.toFixed(2)}`);
        return;
      }
    } else if (splitPayMethod === 'mixed') {
      const cash = parseFloat(splitMixedCash) || 0;
      const transfer = parseFloat(splitMixedTransfer) || 0;
      if (cash + transfer < total) {
        showAlert('error', 'Monto insuficiente', `Recibido: $${(cash + transfer).toFixed(2)} — Total: $${total.toFixed(2)}`);
        return;
      }
    }

    // Agregar unidades por producto (re-combinar unidades individuales)
    const aggregated = {};
    person.itemIndices.forEach(idx => {
      const unit = splitUnits[idx];
      const key = unit.id || unit.name;
      if (aggregated[key]) {
        aggregated[key].quantity += 1;
        aggregated[key].subtotal = parseFloat((aggregated[key].quantity * aggregated[key].price).toFixed(2));
      } else {
        aggregated[key] = {
          id: unit.id,
          name: unit.name,
          price: unit.price,
          quantity: 1,
          use_stock: unit.use_stock,
          subtotal: unit.price,
          _origIdx: unit._origIdx
        };
      }
    });
    const splitItems = Object.values(aggregated);

    setSplitProcessing(true);
    try {
      // ⭐ En modo 'mixed' se manda 0 (no null) si un campo quedó vacío --
      // mismo criterio que MesasManager.js: en un pago mixto ambos campos
      // representan montos reales cobrados, así que "vacío" es "$0 por
      // este método", no "no aplica" (eso sí es null, y sigue siendo así
      // para 'cash'/'transfer' puros, donde el otro método no aplica).
      const cashR = splitPayMethod === 'cash' ? (parseFloat(splitCash) || null) : splitPayMethod === 'mixed' ? (parseFloat(splitMixedCash) || 0) : null;
      const transferR = splitPayMethod === 'transfer' ? total : splitPayMethod === 'mixed' ? (parseFloat(splitMixedTransfer) || 0) : null;
      const changeR = splitPayMethod === 'cash'
        ? Math.max(0, (parseFloat(splitCash) || 0) - total)
        : splitPayMethod === 'mixed'
          ? Math.max(0, (parseFloat(splitMixedCash) || 0) + (parseFloat(splitMixedTransfer) || 0) - total)
          : null;

      // ⭐ FIX: antes se creaba la porción pagada con POST /api/pos/sales
      // genérico (que SIEMPRE descuenta stock al crear cualquier venta), lo
      // cual descontaba el stock DOS VECES para lo mismo -- una al crear el
      // pedido original, otra aquí. Y el pedido original solo se anulaba
      // (restaurando todo su stock) hasta que TODOS terminaban de pagar, así
      // que si se cerraba el modal a la mitad, ese pedido seguía "pending"
      // con la lista completa de items, duplicando el conteo en
      // Reportes/Ventas de hoy. Este endpoint dedicado crea la porción
      // pagada SIN volver a descontar stock, y resta esas mismas unidades
      // del pedido original de inmediato.
      const splitResponse = await axios.post(`${API_URL}/api/pos/sales/${splitOrder.id}/split-pay`, {
        total,
        payment_method: splitPayMethod,
        cash_received: cashR,
        transfer_amount: transferR,
        items: splitItems,
        customer_name: person.name,
      }, { headers: { Authorization: `Bearer ${token}` } });

      const updatedPersons = splitPersons.map((p, i) =>
        i === personIdx ? { ...p, paymentDone: true } : p
      );
      const allPaid = updatedPersons.every(p => p.paymentDone || p.itemIndices.length === 0) || splitResponse.data?.emptied;

      // ⭐ No se cierra el modal de split ni se marca la persona como pagada
      // en la UI todavia: eso se difiere a onDone, que corre despues de que
      // el usuario decide si imprime el recibo de esta porcion.
      setPendingReceipt({
        ticket_ref: `Pedido ${splitOrder.id} · ${person.name}`,
        items: splitItems,
        total,
        payment_method: splitPayMethod,
        cash_received: cashR,
        transfer_amount: transferR,
        change_given: changeR,
        order_type: splitOrder.order_type,
        customer_name: person.name,
        created_at: new Date().toISOString(),
        onDone: async () => {
          setSplitPayingIdx(null);
          setSplitCash('');
          setSplitTransfer('');
          setSplitMixedCash('');
          setSplitMixedTransfer('');
          setSplitPayMethod('cash');
          setPendingReceipt(null);
          if (allPaid) {
            // El pedido original ya quedó vacío/anulado en el backend como
            // parte del mismo split-pay -- no hace falta un segundo request.
            showAlert('success', '¡Cuenta desglosada!', ' Todos los pagos procesados correctamente.');
            setShowSplitModal(false);
            setSplitOrder(null);
          } else {
            setSplitPersons(updatedPersons);
            showAlert('success', 'Pago registrado', `${person.name} pagó $${total.toFixed(2)}`);
          }
          loadPendingOrders();
          loadTables();
        }
      });
    } catch (error) {
      showAlert('error', 'Error al pagar', error.response?.data?.error || 'No se pudo procesar el pago');
    } finally {
      setSplitProcessing(false);
    }
  };

  // ── HELPERS UI ─────────────────────────────────────────────────────

  const getChange = () => {
    if (!selectedOrder) return 0;
    return Math.max(0, (parseFloat(cashReceived) || 0) - selectedOrder.total);
  };

  const getMixedChange = () => {
    if (!selectedOrder) return 0;
    return Math.max(0, ((parseFloat(mixedCash) || 0) + (parseFloat(mixedTransfer) || 0)) - selectedOrder.total);
  };

  const sinMesa = pendingOrders.filter(o => !o.table_id && o.order_type === 'servir');
  const conMesa = pendingOrders.filter(o => o.table_id);
  const paraLlevar = pendingOrders.filter(o => o.order_type === 'llevar');

  const filteredOrders = filterType === 'all'
    ? pendingOrders
    : filterType === 'sin_mesa' ? sinMesa
    : filterType === 'con_mesa' ? conMesa
    : paraLlevar;

  // ══════════════════════════════════════════════════════════════════
  return (
    <div className="space-y-6">
      {customAlert && (
        <CustomAlert
          type={customAlert.type}
          title={customAlert.title}
          message={customAlert.message}
          onClose={() => setCustomAlert(null)}
          zIndex={80}
        />
      )}
      <ReciboManager pendingReceipt={pendingReceipt} businessName={clientData?.business_name} clientPhone={clientData?.phone} token={token} />

      {confirmCancel !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Anular pedido #{confirmCancel}</h3>
            <p className="text-gray-600 text-sm mb-3">Esta acción no se puede deshacer. El stock será restaurado.</p>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Motivo de anulación *</label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Ej: Cliente canceló, error en la orden, producto agotado..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                rows={3}
                autoFocus
              />
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setConfirmCancel(null); setCancelReason(''); }} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancelar</button>
              <button onClick={() => executeCancelOrder(confirmCancel)} className={`flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold ${!cancelReason.trim() ? 'opacity-50 cursor-not-allowed' : ''}`}>Anular</button>
            </div>
          </div>
        </div>
      )}

      {confirmRemoveItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Anular producto</h3>
            <p className="text-gray-600 text-sm mb-4">
              ¿Anular <strong>{confirmRemoveItem.quantity}x {confirmRemoveItem.name}</strong> de este pedido? El stock se restaura.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmRemoveItem(null)} disabled={removingItem} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50">Cancelar</button>
              <button onClick={executeRemoveItem} disabled={removingItem} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold disabled:opacity-50">
                {removingItem ? 'Anulando...' : 'Anular'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ENCABEZADO Y FILTROS ── */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardList className="w-8 h-8 text-yellow-600" />
            Pedidos Pendientes de Pago
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {pendingOrders.length} pedido{pendingOrders.length !== 1 ? 's' : ''} pendiente{pendingOrders.length !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="flex gap-2 flex-wrap">
          {[
            { id: 'all', label: `Todos (${pendingOrders.length})`, color: 'bg-yellow-600' },
            { id: 'sin_mesa', label: ` Sin Mesa (${sinMesa.length})`, color: 'bg-purple-600' },
            { id: 'con_mesa', label: `️ Con Mesa (${conMesa.length})`, color: 'bg-blue-600' },
            { id: 'llevar', label: ` Para Llevar (${paraLlevar.length})`, color: 'bg-orange-600' },
          ].map(f => (
            <button key={f.id} onClick={() => setFilterType(f.id)}
              className={`px-4 py-2 rounded-lg font-semibold transition ${filterType === f.id ? `${f.color} text-white` : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── LISTA DE PEDIDOS ── */}
      {filteredOrders.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <p className="text-xl font-semibold text-gray-700">¡Todo al día!</p>
          <p className="text-gray-500 mt-2">No hay pedidos pendientes de pago</p>
          {onNavigateTo && (
            <button onClick={() => onNavigateTo('caja')}
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition">
              Ir a Caja
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredOrders.map((order) => {
            const items = Array.isArray(order.items) ? order.items : JSON.parse(order.items || '[]');
            const tableName = order.table_name || (order.table_id ? tables.find(t => t.id === order.table_id)?.name || `Mesa ${order.table_id}` : null);

            return (
              <div key={order.id} className={`bg-white rounded-lg shadow-md border-2 overflow-hidden ${
                !order.table_id && order.order_type === 'servir' ? 'border-purple-500'
                : order.order_type === 'llevar' ? 'border-orange-500'
                : 'border-yellow-500'
              }`}>
                <div className={`p-4 border-b ${
                  !order.table_id && order.order_type === 'servir' ? 'bg-purple-50 border-purple-200'
                  : order.order_type === 'llevar' ? 'bg-orange-50 border-orange-200'
                  : 'bg-yellow-50 border-yellow-200'
                }`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-semibold text-gray-700">
                        Pedido <span className="bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded font-bold">{getDayCode(order)}</span>
                        <span className="text-gray-400 ml-1 text-xs">(#{order.id})</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        <Clock className="w-3 h-3 inline mr-1" />{formatEcuadorTime(order.created_at)}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <div className={`px-3 py-1 rounded-full text-xs font-bold ${order.order_type === 'servir' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                        {order.order_type === 'servir' ? '️ Servir' : ' Llevar'}
                      </div>
                      {tableName && <div className="px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700"> {tableName}</div>}
                      {!order.table_id && order.order_type === 'servir' && (
                        <div className="px-3 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-700">️ Sin mesa</div>
                      )}
                    </div>
                  </div>
                  {order.customer_name && (
                    <p className="text-sm font-semibold text-gray-800 mt-2"> {order.customer_name}</p>
                  )}
                </div>

                <div className="p-4">
                  <div className="space-y-2 mb-3">
                    {items.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm gap-2">
                        <span className="text-gray-700 flex-1 min-w-0">{item.quantity}x {item.name}</span>
                        <span className="font-semibold text-gray-900 flex-shrink-0">${((item.price || 0) * (item.quantity || 0)).toFixed(2)}</span>
                        <button
                          onClick={() => setConfirmRemoveItem({ orderId: order.id, origIdx: idx, name: item.name, quantity: item.quantity })}
                          className="flex-shrink-0 p-1 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded transition"
                          title="Anular este producto"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="border-t pt-3 mb-4">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-gray-700">TOTAL:</span>
                      <span className="text-2xl font-bold text-yellow-600">${order.total?.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Asignar mesa si no tiene */}
                  {!order.table_id && order.order_type === 'servir' && (
                    <div className="mb-3">
                      <button
                        onClick={() => setShowAssignModal(order.id)}
                        className="w-full py-2 px-3 bg-purple-100 text-purple-700 border-2 border-purple-300 rounded-lg hover:bg-purple-200 transition font-semibold text-sm flex items-center justify-center gap-2"
                      >
                        <Users className="w-4 h-4" /> Asignar Mesa
                      </button>
                    </div>
                  )}

                  {/* Acciones */}
                  <div className="flex gap-2">
                    <button onClick={() => handlePayOrder(order)}
                      className="flex-1 bg-green-600 text-white py-2 px-3 rounded-lg hover:bg-green-700 transition flex items-center justify-center gap-1 font-semibold text-sm">
                      <DollarSign className="w-4 h-4" />Cobrar
                    </button>
                    <button onClick={() => openSplitModal(order)}
                      className="bg-blue-100 text-blue-700 py-2 px-3 rounded-lg hover:bg-blue-200 transition flex items-center gap-1 font-semibold text-sm"
                      title="Desglosar cuenta">
                      <Scissors className="w-4 h-4" />Desglosar
                    </button>
                    <button onClick={() => handleCancelOrder(order.id)}
                      className="bg-red-100 text-red-700 py-2 px-3 rounded-lg hover:bg-red-200 transition"
                      title="Anular pedido">
                      <XCircle className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          MODAL DE PAGO NORMAL
      ══════════════════════════════════════════════════════════════ */}
      {showPaymentModal && selectedOrder && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closePaymentModal}
        >
          <div
            className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-5 border-b flex justify-between items-center sticky top-0 bg-white rounded-t-xl">
              <div>
                <h3 className="text-xl font-bold text-gray-800">Procesar Pago</h3>
                <p className="text-sm text-gray-500">Pedido #{selectedOrder.id}</p>
                {selectedOrder.customer_name && (
                  <p className="text-sm text-gray-600">Cliente: {selectedOrder.customer_name}</p>
                )}
              </div>
              <button
                onClick={closePaymentModal}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-5">
              <div className="bg-green-50 rounded-xl p-4 text-center mb-5">
                <p className="text-3xl font-bold text-green-600">${selectedOrder.total?.toFixed(2)}</p>
                {selectedOrder.table_name && (
                  <p className="text-xs text-blue-600 mt-1">🪑 Mesa: {selectedOrder.table_name}</p>
                )}
              </div>

              <h4 className="font-semibold mb-3">Método de Pago</h4>
              <div className="grid grid-cols-3 gap-3 mb-5">
                {[
                  { id: 'cash', label: ' Efectivo', active: 'border-green-500 bg-green-50 text-green-700' },
                  { id: 'transfer', label: ' Transfer', active: 'border-blue-500 bg-blue-50 text-blue-700' },
                  { id: 'mixed', label: ' Mixto', active: 'border-purple-500 bg-purple-50 text-purple-700' },
                ].map(m => (
                  <button key={m.id}
                    onClick={() => { setPaymentMethod(m.id); setCashReceived(''); setMixedCash(''); setMixedTransfer(''); }}
                    className={`p-3 border-2 rounded-lg font-semibold text-sm transition ${paymentMethod === m.id ? m.active : 'border-gray-300 hover:border-gray-400'}`}>
                    {m.label}
                  </button>
                ))}
              </div>

              {paymentMethod === 'cash' && (
                <div className="mb-4">
                  <label className="block font-semibold mb-2"> Efectivo recibido *</label>
                  <input type="number" step="0.01" value={cashReceived}
                    onChange={(e) => setCashReceived(e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg text-xl" placeholder="0.00" autoFocus />
                  {cashReceived && (
                    <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-sm text-gray-600"> Cambio: <strong className="text-green-600 text-xl">${getChange().toFixed(2)}</strong></p>
                    </div>
                  )}
                </div>
              )}

              {paymentMethod === 'transfer' && (
                <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-gray-600">Monto a transferir:</p>
                  <p className="text-2xl font-bold text-blue-600">${selectedOrder.total?.toFixed(2)}</p>
                </div>
              )}

              {paymentMethod === 'mixed' && (
                <div className="mb-4 space-y-3">
                  <div>
                    <label className="block font-semibold mb-1"> Efectivo *</label>
                    <input type="number" step="0.01" value={mixedCash}
                      onChange={(e) => setMixedCash(e.target.value)}
                      className="w-full px-4 py-3 border rounded-lg text-xl" placeholder="0.00" />
                  </div>
                  <div>
                    <label className="block font-semibold mb-1"> Transferencia *</label>
                    <input type="number" step="0.01" value={mixedTransfer}
                      onChange={(e) => setMixedTransfer(e.target.value)}
                      className="w-full px-4 py-3 border rounded-lg text-xl" placeholder="0.00" />
                  </div>
                  {mixedCash && mixedTransfer && (
                    <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <p className="text-sm text-gray-600"> Cambio: <strong className="text-green-600 text-xl">${getMixedChange().toFixed(2)}</strong></p>
                    </div>
                  )}
                </div>
              )}

              <button onClick={processPayment}
                className="w-full bg-green-600 text-white py-4 rounded-lg font-bold text-lg hover:bg-green-700 transition">
                Confirmar Pago
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          MODAL DE ASIGNAR MESA
      ══════════════════════════════════════════════════════════════ */}
      {showAssignModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowAssignModal(null)}
        >
          <div
            className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b bg-gradient-to-r from-purple-600 to-blue-600 rounded-t-xl">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <Users className="w-6 h-6" /> Asignar Mesa
                  </h3>
                  <p className="text-purple-200 text-sm mt-0.5">Pedido #{showAssignModal}</p>
                </div>
                <button onClick={() => setShowAssignModal(null)} className="text-white hover:bg-white hover:bg-opacity-20 rounded-full w-8 h-8 flex items-center justify-center text-xl font-bold">×</button>
              </div>
            </div>

            <div className="p-5">
              {/* Mesas Disponibles */}
              <div className="mb-6">
                <h5 className="text-sm font-semibold text-green-700 mb-3 flex items-center gap-2">
                  <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                  MESAS DISPONIBLES ({tables.filter(t => t.status === 'available').length})
                </h5>
                {tables.filter(t => t.status === 'available').length > 0 ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {tables.filter(t => t.status === 'available').map((table) => (
                      <button
                        key={table.id}
                        onClick={async () => {
                          await handleAssignTable(showAssignModal, table.id);
                          setShowAssignModal(null);
                        }}
                        className="p-4 bg-green-100 border-2 border-green-500 rounded-lg hover:bg-green-200 hover:scale-105 transition-all text-center"
                      >
                        <Users className="w-6 h-6 mx-auto mb-1 text-green-700" />
                        <p className="font-bold text-green-900 text-sm">{table.name}</p>
                        <p className="text-xs text-green-600">Disponible</p>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm italic py-3">No hay mesas disponibles</p>
                )}
              </div>

              {/* Mesas Ocupadas */}
              <div>
                <h5 className="text-sm font-semibold text-orange-700 mb-3 flex items-center gap-2">
                  <span className="w-3 h-3 bg-orange-500 rounded-full"></span>
                  MESAS OCUPADAS ({tables.filter(t => t.status === 'occupied').length})
                  <span className="text-xs font-normal text-gray-500">— agregar a cuenta existente</span>
                </h5>
                {tables.filter(t => t.status === 'occupied').length > 0 ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {tables.filter(t => t.status === 'occupied').map((table) => (
                      <button
                        key={table.id}
                        onClick={async () => {
                          await handleAssignTable(showAssignModal, table.id);
                          setShowAssignModal(null);
                        }}
                        className="p-4 bg-orange-100 border-2 border-orange-400 rounded-lg hover:bg-orange-200 hover:scale-105 transition-all text-center"
                      >
                        <Users className="w-6 h-6 mx-auto mb-1 text-orange-700" />
                        <p className="font-bold text-orange-900 text-sm">{table.name}</p>
                        <p className="text-xs text-orange-600">{table.order_count || 0} orden{(table.order_count || 0) !== 1 ? 'es' : ''}</p>
                        {table.total > 0 && <p className="text-xs font-bold text-orange-800">${Number(table.total).toFixed(2)}</p>}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm italic py-3">No hay mesas ocupadas</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          MODAL DE DESGLOSAR CUENTA
      ══════════════════════════════════════════════════════════════ */}
      {showSplitModal && splitOrder && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
          onClick={() => { if (splitPayingIdx === null) setShowSplitModal(false); }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white rounded-t-2xl border-b p-5 flex justify-between items-center z-10">
              <div>
                <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <Scissors className="w-6 h-6 text-blue-600" />
                  Desglosar Cuenta
                </h3>
                <p className="text-sm text-gray-500">
                  Pedido #{splitOrder.id} · Total: <strong className="text-green-600">${splitOrder.total?.toFixed(2)}</strong>
                </p>
              </div>
              <button onClick={() => setShowSplitModal(false)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* Controles */}
              <div className="flex gap-2 flex-wrap">
                <button onClick={splitRepartirEquitativamente}
                  className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200 transition">
                  ⚖️ Repartir equitativamente
                </button>
                {splitPersons.length < 4 && (
                  <button onClick={addSplitPerson}
                    className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-sm font-semibold hover:bg-blue-200 transition">
                    + Agregar persona
                  </button>
                )}
                {splitHistory.length > 0 && (
                  <button onClick={undoSplitAssignment}
                    className="px-3 py-1.5 bg-orange-100 text-orange-700 rounded-lg text-sm font-semibold hover:bg-orange-200 transition flex items-center gap-1">
                    <Undo2 className="w-3.5 h-3.5" /> Deshacer ({splitHistory.length})
                  </button>
                )}
              </div>

              {/* Unidades individuales y asignación */}
              <div className="border rounded-xl overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b">
                  <p className="text-xs font-bold text-gray-600 uppercase tracking-wide">Asignar unidades a cada persona ({splitUnits.length} unidades)</p>
                </div>
                <div className="divide-y max-h-60 overflow-y-auto">
                  {splitUnits.map((unit, unitIdx) => {
                    const assignedTo = splitPersons.findIndex(p => p.itemIndices.includes(unitIdx));
                    return (
                      <div key={unitIdx} className="px-4 py-2.5 flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-800 text-sm truncate">
                            {unit.name}
                            {unit._totalUnits > 1 && (
                              <span className="text-gray-400 text-xs ml-1">({unit._unitNum}/{unit._totalUnits})</span>
                            )}
                          </p>
                          <p className="text-xs text-gray-500">${(unit.price || 0).toFixed(2)}</p>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          {splitPersons.map((person, pIdx) => {
                            const isAssigned = assignedTo === pIdx;
                            const colors = PERSON_COLORS[pIdx];
                            return (
                              <button
                                key={pIdx}
                                onClick={() => !person.paymentDone && assignItemToPerson(pIdx, unitIdx)}
                                disabled={person.paymentDone}
                                className={`w-8 h-8 rounded-full text-xs font-black transition ${
                                  isAssigned
                                    ? `${colors.label} text-white shadow-md scale-110`
                                    : person.paymentDone
                                    ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                                    : `bg-gray-100 text-gray-500 hover:${colors.bg}`
                                }`}
                                title={person.name}
                              >
                                P{pIdx + 1}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Personas y pagos */}
              <div className="space-y-3">
                {splitPersons.map((person, pIdx) => {
                  const total = parseFloat(getSplitPersonTotal(pIdx).toFixed(2));
                  const colors = PERSON_COLORS[pIdx];
                  const isPaying = splitPayingIdx === pIdx;

                  return (
                    <div key={pIdx}
                      className={`rounded-xl border-2 overflow-hidden ${person.paymentDone ? 'border-green-400 bg-green-50' : `${colors.border} ${colors.bg}`}`}>
                      <div className="p-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black text-white ${colors.label}`}>
                            P{pIdx + 1}
                          </span>
                          <div>
                            <input
                              type="text"
                              value={person.name}
                              onChange={(e) => setSplitPersons(prev => prev.map((p, i) => i === pIdx ? { ...p, name: e.target.value } : p))}
                              className={`font-bold text-sm bg-transparent border-b border-dashed ${colors.text} w-24 focus:outline-none`}
                              disabled={person.paymentDone}
                            />
                            <p className="text-xs text-gray-500">{person.itemIndices.length} unidad{person.itemIndices.length !== 1 ? 'es' : ''} · {
                              person.itemIndices.map(i => splitUnits[i]?.name).filter(Boolean).join(', ').substring(0, 40) || '—'
                            }</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-lg font-black ${colors.text}`}>${total.toFixed(2)}</span>
                          {person.paymentDone ? (
                            <span className="text-green-600 font-bold text-sm flex items-center gap-1">
                              <CheckCircle className="w-5 h-5" /> Pagado
                            </span>
                          ) : (
                            <div className="flex gap-1">
                              {splitPersons.length > 2 && (
                                <button onClick={() => removeSplitPerson(pIdx)}
                                  className="text-gray-400 hover:text-red-500 text-xs px-1">✕</button>
                              )}
                              <button
                                onClick={() => { setSplitPayingIdx(isPaying ? null : pIdx); setSplitPayMethod('cash'); setSplitCash(''); setSplitTransfer(''); setSplitMixedCash(''); setSplitMixedTransfer(''); }}
                                disabled={person.itemIndices.length === 0 || total <= 0}
                                className={`px-3 py-1.5 rounded-lg text-xs font-bold text-white transition disabled:opacity-40 ${colors.btn}`}>
                                {isPaying ? 'Cancelar' : 'Cobrar'}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Formulario de pago inline */}
                      {isPaying && !person.paymentDone && (
                        <div className="border-t p-3 bg-white space-y-3">
                          <div className="flex gap-2">
                            {[
                              { id: 'cash', label: ' Efectivo' },
                              { id: 'transfer', label: ' Transfer' },
                              { id: 'mixed', label: ' Mixto' },
                            ].map(m => (
                              <button key={m.id}
                                onClick={() => { setSplitPayMethod(m.id); setSplitCash(''); setSplitTransfer(''); setSplitMixedCash(''); setSplitMixedTransfer(''); }}
                                className={`flex-1 py-1.5 border-2 rounded-lg text-xs font-semibold transition ${splitPayMethod === m.id ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300'}`}>
                                {m.label}
                              </button>
                            ))}
                          </div>

                          {splitPayMethod === 'cash' && (
                            <div>
                              <label className="text-xs font-semibold text-gray-600">Efectivo recibido</label>
                              <input type="number" step="0.01" value={splitCash} onChange={(e) => setSplitCash(e.target.value)}
                                className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" placeholder="0.00" autoFocus />
                              {splitCash && <p className="text-xs text-green-600 mt-1 font-semibold">Cambio: ${Math.max(0, (parseFloat(splitCash) || 0) - total).toFixed(2)}</p>}
                            </div>
                          )}
                          {splitPayMethod === 'transfer' && (
                            <div className="p-2 bg-blue-50 rounded-lg text-xs text-blue-700">
                              Transferencia de <strong>${total.toFixed(2)}</strong>
                            </div>
                          )}
                          {splitPayMethod === 'mixed' && (
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="text-xs font-semibold text-gray-600">Efectivo</label>
                                <input type="number" step="0.01" value={splitMixedCash} onChange={(e) => setSplitMixedCash(e.target.value)}
                                  className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" placeholder="0.00" />
                              </div>
                              <div>
                                <label className="text-xs font-semibold text-gray-600">Transferencia</label>
                                <input type="number" step="0.01" value={splitMixedTransfer} onChange={(e) => setSplitMixedTransfer(e.target.value)}
                                  className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" placeholder="0.00" />
                              </div>
                              {splitMixedCash && splitMixedTransfer && (
                                <p className="col-span-2 text-xs text-green-600 font-semibold">
                                  Cambio: ${Math.max(0, (parseFloat(splitMixedCash) || 0) + (parseFloat(splitMixedTransfer) || 0) - total).toFixed(2)}
                                </p>
                              )}
                            </div>
                          )}

                          <button
                            onClick={() => handlePaySplitPortion(pIdx)}
                            disabled={splitProcessing}
                            className="w-full py-2 bg-green-600 text-white rounded-lg font-bold text-sm hover:bg-green-700 transition disabled:opacity-60">
                            {splitProcessing ? 'Procesando...' : ` Confirmar pago de $${total.toFixed(2)}`}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Verificar unidades sin asignar */}
              {(() => {
                const assignedCount = splitPersons.reduce((sum, p) => sum + p.itemIndices.length, 0);
                if (assignedCount < splitUnits.length) {
                  return (
                    <div className="p-3 bg-orange-50 border border-orange-300 rounded-xl text-sm text-orange-700 font-semibold">
                      ⚠️ {splitUnits.length - assignedCount} unidad(es) sin asignar — asígnalas a una persona antes de cobrar.
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PedidosPendientes;
